/**
 * Broker Analytics — Pure computation functions
 * All data derived from existing broker data (connections, leads, applications).
 * No new API endpoints required.
 */

import type { Connection, Lead, Application } from '../api/brokerApi'
import { KANBAN_COLUMNS, TERMINAL_STAGES } from '../constants/applicationStages'

// ── Types ────────────────────────────────────────────────────────────────────

export interface LeadPipelineEntry {
  status: string
  count: number
}

export interface PhaseEntry {
  phase: string
  count: number
  color: string
}

export interface MonthlyActivityEntry {
  month: string
  leads: number
  connections: number
}

// ── Phase Colors (matching applicationStages PHASE_COLORS) ───────────────────

const PHASE_CHART_COLORS: Record<string, string> = {
  Discovery:  '#3B82F6',
  Research:   '#8B5CF6',
  Documents:  '#F59E0B',
  Submission: '#EF4444',
  Assessment: '#EC4899',
  Approval:   '#10B981',
  Settlement: '#06B6D4',
  'On Hold':  '#F59E0B',
  Closed:     '#6B7280',
}

// ── 1. Lead Pipeline Funnel ─────────────────────────────────────────────────

const LEAD_STATUSES = ['new', 'contacted', 'qualified', 'converted', 'dead']

export function computeLeadPipeline(leads: Lead[]): LeadPipelineEntry[] {
  const counts = new Map<string, number>()
  for (const s of LEAD_STATUSES) counts.set(s, 0)
  for (const lead of leads) {
    const current = counts.get(lead.status) ?? 0
    counts.set(lead.status, current + 1)
  }
  return LEAD_STATUSES.map(status => ({ status, count: counts.get(status) ?? 0 }))
}

// ── 2. Applications by Kanban Phase ─────────────────────────────────────────

export function computeApplicationsByPhase(applications: Application[]): PhaseEntry[] {
  const phaseMap = new Map<string, number>()
  for (const col of KANBAN_COLUMNS) phaseMap.set(col.label, 0)

  for (const app of applications) {
    for (const col of KANBAN_COLUMNS) {
      if (col.stages.includes(app.stage)) {
        phaseMap.set(col.label, (phaseMap.get(col.label) ?? 0) + 1)
        break
      }
    }
  }

  return Array.from(phaseMap.entries()).map(([phase, count]) => ({
    phase,
    count,
    color: PHASE_CHART_COLORS[phase] ?? '#6B7280',
  }))
}

// ── 3. Monthly Activity Timeline ────────────────────────────────────────────

export function computeMonthlyActivity(
  leads: Lead[],
  connections: Connection[],
  months = 6,
  now = new Date(),
): MonthlyActivityEntry[] {
  const result: MonthlyActivityEntry[] = []
  const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']

  for (let i = months - 1; i >= 0; i--) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1)
    const year = d.getFullYear()
    const month = d.getMonth()
    const label = `${monthNames[month]} ${year}`

    const leadCount = leads.filter(l => {
      if (!l.created_at) return false
      const ld = new Date(l.created_at)
      return ld.getFullYear() === year && ld.getMonth() === month
    }).length

    const connCount = connections.filter(c => {
      if (!c.created_at) return false
      const cd = new Date(c.created_at)
      return cd.getFullYear() === year && cd.getMonth() === month
    }).length

    result.push({ month: label, leads: leadCount, connections: connCount })
  }

  return result
}

// ── 4. Conversion Rate ──────────────────────────────────────────────────────

export function computeConversionRate(leads: Lead[]): number {
  if (leads.length === 0) return 0
  const converted = leads.filter(l => l.status === 'converted').length
  return Math.round((converted / leads.length) * 100)
}

// ── 5. Average Loan Amount ──────────────────────────────────────────────────

export function computeAverageLoanAmount(applications: Application[]): number {
  const amounts = applications
    .map(a => a.loan_amount)
    .filter((a): a is number => a !== null && a !== undefined)
  if (amounts.length === 0) return 0
  return Math.round(amounts.reduce((sum, a) => sum + a, 0) / amounts.length)
}

// ── 6. Pipeline Value (active applications only) ────────────────────────────

export function computePipelineValue(applications: Application[]): number {
  return applications
    .filter(a => !TERMINAL_STAGES.includes(a.stage))
    .reduce((sum, a) => sum + (a.loan_amount ?? 0), 0)
}
